// Evidence-graph hooks for the SPA.
//
// Two surfaces the UI subscribes to:
//   1. Per-event proof state — drives the badge on each timeline row.
//   2. Aggregate gaps summary — drives the "Evidence to gather" banner
//      that sits above the timeline.
//
// Both are read-only in phase 1b. Phase 2 adds link/unlink mutations.

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './api';
import { useMe } from './auth';

export type ProofStatus =
  | 'self-proving'
  | 'document-backed'
  | 'partial'
  | 'proof-missing'
  | 'not-applicable';

export type EvidenceRequirement = {
  needsProof: boolean;
  selfProving: boolean;
  suggestedDocCategories: string[];
  rationale: string;
};

export type EventProofState = {
  eventId: string;
  eventType: string;
  eventDate: string;
  eventSummary: string;
  sourceFileCategory: string | null;
  eventSource: 'chat' | 'manual' | 'whatsapp' | 'document';
  status: ProofStatus;
  requirement: EvidenceRequirement;
  documentaryProofCount: number;
  corroboratingProofCount: number;
};

export type EvidenceGapsSummary = {
  /** Every proof-missing event. Used by per-event ⚠ badges on the
   *  timeline so curious users can see all gaps. */
  gaps: EventProofState[];
  /** Subset that warrants banner real-estate. Filter is contextual:
   *  agreements always; payments scaled to contract total or user
   *  intent. The banner reads this list so 40+ low-stakes gaps don't
   *  dominate the page. */
  highStakesGaps: EventProofState[];
  totalActionable: number;
  totalCovered: number;
};

export const evidenceStateQueryKey = (caseId: string) =>
  ['cases', caseId, 'evidence', 'state'] as const;
export const evidenceGapsQueryKey = (caseId: string) =>
  ['cases', caseId, 'evidence', 'gaps'] as const;

/**
 * Per-event proof state for the case. Used by the timeline pane to
 * decorate each row with a proof badge.
 *
 * Returned as a Map keyed by eventId for O(1) lookup in the row
 * renderer — building the map in `select` keeps the query cache holding
 * the canonical shape (array) and the consumer holding the lookup
 * shape, which lets two callers share the same fetch.
 */
export function useEvidenceState(caseId: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: evidenceStateQueryKey(caseId ?? ''),
    queryFn: () => apiFetch<{ states: EventProofState[] }>(`/cases/${caseId}/evidence/state`),
    enabled: !!caseId && !!me.data?.user,
    select: (d) => {
      const byEvent = new Map<string, EventProofState>();
      for (const s of d.states) byEvent.set(s.eventId, s);
      return { states: d.states, byEvent };
    },
  });
}

/**
 * Aggregate gap summary for the "Evidence to gather" banner.
 *
 * The banner only renders when `gaps.length > 0`, so callers don't need
 * a loading state for the empty case — just check the array.
 */
export function useEvidenceGaps(caseId: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: evidenceGapsQueryKey(caseId ?? ''),
    queryFn: () => apiFetch<EvidenceGapsSummary>(`/cases/${caseId}/evidence/gaps`),
    enabled: !!caseId && !!me.data?.user,
  });
}

/**
 * Per-event "I don't need to act on this" dismissal for inline evidence-gap
 * suggestions. Backed by localStorage so the user's choices stick across
 * reloads. Per-device is fine — this is UI noise reduction, not legal
 * state we'd want to sync to the server.
 *
 * Why we need this: the deterministic event-type extractor sometimes
 * mislabels (e.g. "homeowner refuses to make the bank transfer" gets
 * tagged as `payment`, which prompts "upload an invoice" — but there's
 * nothing to upload because the payment didn't happen). Letting the user
 * dismiss the suggestion is cheaper than making the extractor perfect.
 *
 * Consumed in two places:
 *   1. timeline-pane.tsx — hides the inline ⚠ EvidenceGapInline.
 *   2. artifacts-pane.tsx — subtracts dismissed events from the pack
 *      readiness gate (otherwise the user dismisses the prompt on the
 *      timeline but the pack card still says "Almost ready — finalise
 *      these to unlock", which is exactly the bug we're fixing here).
 *
 * Future: if we ever need cross-device persistence, promote this into a
 * server-side `event_proof_dismissed` table + endpoint. Until then,
 * localStorage is fine for the single-device usage profile.
 */
export function useDismissedGaps(caseId: string) {
  const key = `claimpack:dismissed-gaps:${caseId}`;
  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed(key));

  // Sync across components in the same tab — both timeline-pane and
  // artifacts-pane subscribe via this hook, and a dismiss in one needs to
  // visually clear in the other without a route change.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== key) return;
      setDismissed(readDismissed(key));
    }
    function onCustom(e: Event) {
      const detail = (e as CustomEvent<{ key?: string }>).detail;
      if (!detail || detail.key !== key) return;
      setDismissed(readDismissed(key));
    }
    window.addEventListener('storage', onStorage);
    window.addEventListener('claimpack:dismissed-gaps-changed', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('claimpack:dismissed-gaps-changed', onCustom);
    };
  }, [key]);

  const dismiss = useCallback(
    (id: string) => {
      setDismissed((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        try {
          localStorage.setItem(key, JSON.stringify(Array.from(next)));
          // `storage` events only fire on OTHER tabs, not the originating
          // one. Custom event lets sibling components in the same tab
          // re-read state without prop-drilling.
          window.dispatchEvent(
            new CustomEvent('claimpack:dismissed-gaps-changed', { detail: { key } }),
          );
        } catch {
          /* quota / private mode — silent fallback to in-memory */
        }
        return next;
      });
    },
    [key],
  );

  return { dismissed, dismiss };
}

function readDismissed(key: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(key);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}
